import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlMatchResult,
  UrlSegment,
  UrlTree,
} from "@angular/router";
import { APP_PATHS } from "./app-route-paths";
import { applyContactQuickSearch } from "./contact-search.util";

type LegacyRule = readonly [RegExp, (match: RegExpMatchArray) => string];

const ref = (match: RegExpMatchArray, index = 1): string => {
  try {
    return decodeURIComponent(match[index]);
  } catch {
    return match[index];
  }
};

const LEGACY_RULES: readonly LegacyRule[] = [
  [
    /^\/(?:analytics-chat|analytics|dashboard\/stats)\/?$/,
    () => APP_PATHS.dashboard,
  ],
  [/^\/team-inbox(?:\/page)?\/?$/, () => APP_PATHS.messaging.inbox],
  [/^\/contact(?:\/list)?\/?$/, () => APP_PATHS.messaging.contacts],
  [/^\/contact\/create\/?$/, () => APP_PATHS.messaging.contactNew],
  [/^\/contact\/upload\/?$/, () => APP_PATHS.messaging.contactImport],
  [/^\/contact\/view\/([^/]+)\/?$/, (m) => APP_PATHS.messaging.contact(ref(m))],
  [
    /^\/contact\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.messaging.contactEdit(ref(m)),
  ],
  [/^\/manage-ticket\/?$/, () => APP_PATHS.messaging.tickets],
  [/^\/manage-ticket\/list\/?$/, () => APP_PATHS.messaging.tickets],
  [/^\/manage-ticket\/create\/?$/, () => APP_PATHS.messaging.ticketNew],
  [
    /^\/manage-ticket\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.messaging.ticket(ref(m)),
  ],
  [
    /^\/manage-ticket\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.messaging.ticketEdit(ref(m)),
  ],
  [/^\/messaging\/tickets\/create\/?$/, () => APP_PATHS.messaging.ticketNew],
  [
    /^\/messaging\/tickets\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.messaging.ticket(ref(m)),
  ],
  [
    /^\/messaging\/tickets\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.messaging.ticketEdit(ref(m)),
  ],
  [/^\/order(?:\/list)?\/?$/, () => APP_PATHS.catalog.orders],
  [/^\/order\/create\/?$/, () => APP_PATHS.catalog.orderNew],
  [/^\/order\/create\/([^/]+)\/?$/, (m) => APP_PATHS.catalog.orderFrom(ref(m))],
  [/^\/order\/view\/([^/]+)\/?$/, (m) => APP_PATHS.catalog.order(ref(m))],
  [/^\/order\/edit\/([^/]+)\/?$/, (m) => APP_PATHS.catalog.orderEdit(ref(m))],
  [/^\/reservation\/?$/, () => APP_PATHS.catalog.reservations],
  [/^\/reservation\/create\/?$/, () => APP_PATHS.catalog.reservationNew],
  [
    /^\/reservation\/detail\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.reservation(ref(m)),
  ],
  [
    /^\/reservation\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.reservationEdit(ref(m)),
  ],
  [
    /^\/reservation\/rooms\/?$/,
    () => `${APP_PATHS.catalog.products}?itemType=ROOM`,
  ],
  [
    /^\/reservation\/rooms\/create\/?$/,
    () => `${APP_PATHS.catalog.productNew}?itemType=ROOM`,
  ],
  [
    /^\/reservation\/rooms\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.product(ref(m)),
  ],
  [
    /^\/reservation\/rooms\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.productEdit(ref(m)),
  ],
  [/^\/product(?:\/list)?\/?$/, () => APP_PATHS.catalog.products],
  [/^\/product\/archived\/?$/, () => APP_PATHS.catalog.productArchived],
  [/^\/product\/create\/?$/, () => APP_PATHS.catalog.productNew],
  [/^\/product\/upload\/?$/, () => APP_PATHS.catalog.productImport],
  [/^\/product\/ai-import\/?$/, () => APP_PATHS.catalog.productImport],
  [/^\/product\/view\/([^/]+)\/?$/, (m) => APP_PATHS.catalog.product(ref(m))],
  [
    /^\/product\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.productEdit(ref(m)),
  ],
  [/^\/product\/categories\/?$/, () => APP_PATHS.catalog.categories],
  [
    /^\/product\/categories\/archived\/?$/,
    () => APP_PATHS.catalog.categoryArchived,
  ],
  [/^\/product\/categories\/create\/?$/, () => APP_PATHS.catalog.categoryNew],
  [
    /^\/product\/categories\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.categoryEdit(ref(m)),
  ],
  [/^\/product\/manage-crm\/?$/, () => APP_PATHS.settings.catalogIntegration],
  [/^\/product\/crm-products\/?$/, () => APP_PATHS.settings.catalogProducts],
  [/^\/delivery\/?$/, () => APP_PATHS.catalog.delivery],
  [/^\/delivery\/merchant(?:\/list)?\/?$/, () => APP_PATHS.catalog.merchants],
  [/^\/delivery\/merchant\/create\/?$/, () => APP_PATHS.catalog.merchantNew],
  [
    /^\/delivery\/merchant\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.merchant(ref(m)),
  ],
  [
    /^\/delivery\/merchant\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.merchantEdit(ref(m)),
  ],
  [
    /^\/delivery\/delivery-man(?:\/list)?\/?$/,
    () => APP_PATHS.catalog.couriers,
  ],
  [/^\/delivery\/delivery-man\/create\/?$/, () => APP_PATHS.catalog.courierNew],
  [
    /^\/delivery\/delivery-man\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.courier(ref(m)),
  ],
  [
    /^\/delivery\/delivery-man\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.courierEdit(ref(m)),
  ],
  [/^\/delivery\/parcel(?:\/list)?\/?$/, () => APP_PATHS.catalog.parcels],
  [/^\/delivery\/parcel\/create\/?$/, () => APP_PATHS.catalog.parcelNew],
  [
    /^\/delivery\/parcel\/create\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.parcelFrom(ref(m)),
  ],
  [
    /^\/delivery\/parcel\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.parcel(ref(m)),
  ],
  [
    /^\/delivery\/parcel\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.catalog.parcelEdit(ref(m)),
  ],
  [/^\/voucher(?:\/list)?\/?$/, () => APP_PATHS.catalog.vouchers],
  [/^\/voucher\/import\/?$/, () => APP_PATHS.catalog.voucherImport],
  [/^\/voucher\/barre-code\/?$/, () => APP_PATHS.catalog.voucherBarcodes],
  [/^\/voucher\/brodcast\/?$/, () => APP_PATHS.catalog.voucherBroadcast],
  [/^\/template(?:\/list|\/library)?\/?$/, () => APP_PATHS.workflow.templates],
  [
    /^\/template\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.workflow.template(ref(m)),
  ],
  [
    /^\/template\/request(?:\/list)?\/?$/,
    () => APP_PATHS.workflow.templateRequests,
  ],
  [
    /^\/template\/request\/create\/?$/,
    () => APP_PATHS.workflow.templateRequestNew,
  ],
  [
    /^\/template\/request\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.workflow.templateRequest(ref(m)),
  ],
  [
    /^\/template\/request\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.workflow.templateRequestEdit(ref(m)),
  ],
  [/^\/template\/flow(?:\/list)?\/?$/, () => APP_PATHS.workflow.forms],
  [/^\/template\/flow\/create\/?$/, () => APP_PATHS.workflow.formNew],
  [
    /^\/template\/flow\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.workflow.form(ref(m)),
  ],
  [
    /^\/template\/flow\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.workflow.formEdit(ref(m)),
  ],
  [
    /^\/template\/flow\/responses\/([^/]+)\/?$/,
    (m) => APP_PATHS.workflow.formResponses(ref(m)),
  ],
  [/^\/template\/chat-bot\/?$/, () => APP_PATHS.workflow.bots],
  [
    /^\/template\/chat-bot\/([^/]+)\/workflow\/?$/,
    (m) => APP_PATHS.workflow.botWorkflow(ref(m)),
  ],
  [
    /^\/automations(?:\/default-action)?\/?$/,
    () => APP_PATHS.workflow.autoResponses,
  ],
  [/^\/automations\/notification\/?$/, () => APP_PATHS.settings.notifications],
  [
    /^\/automations\/flows(?:\/list)?\/?$/,
    () => APP_PATHS.workflow.automationFlows,
  ],
  [
    /^\/automations\/flows\/create\/?$/,
    () => APP_PATHS.workflow.automationFlowNew,
  ],
  [/^\/automations\/channel\/?$/, () => APP_PATHS.workflow.channelMessages],
  [
    /^\/automations\/pending-message\/?$/,
    () => APP_PATHS.workflow.pendingMessages,
  ],
  [
    /^\/automations\/pending-message\/([^/]+)\/?$/,
    (m) => APP_PATHS.workflow.pendingMessage(ref(m)),
  ],
  [/^\/automations\/ai-agents\/?$/, () => APP_PATHS.workflow.aiAgents],
  [/^\/automations\/ai-assistance\/?$/, () => APP_PATHS.workflow.aiAssistance],
  [/^\/broadcast(?:\/overview)?\/?$/, () => APP_PATHS.campaigns.history],
  [/^\/broadcast\/create\/?$/, () => APP_PATHS.campaigns.new],
  [/^\/broadcast\/scheduled\/?$/, () => APP_PATHS.campaigns.history],
  [
    /^\/broadcast\/overview\/([^/]+)\/?$/,
    (m) => APP_PATHS.campaigns.detail(ref(m)),
  ],
  [/^\/broadcast\/edit\/([^/]+)\/?$/, (m) => APP_PATHS.campaigns.edit(ref(m))],
  [/^\/payment(?:\/list)?\/?$/, () => APP_PATHS.payment.links],
  [/^\/payment\/authorization\/?$/, () => APP_PATHS.payment.authorizations],
  [/^\/payment\/saved-cards\/?$/, () => APP_PATHS.payment.savedCards],
  [/^\/payment\/payment-simulator\/?$/, () => APP_PATHS.payment.simulator],
  [
    /^\/administration\/general-parameters\/?$/,
    () => APP_PATHS.settings.company,
  ],
  [/^\/administration\/agent(?:\/list)?\/?$/, () => APP_PATHS.settings.agents],
  [/^\/administration\/agent\/create\/?$/, () => APP_PATHS.settings.agentNew],
  [
    /^\/administration\/agent\/archived\/?$/,
    () => APP_PATHS.settings.agentArchived,
  ],
  [
    /^\/administration\/agent\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.settings.agent(ref(m)),
  ],
  [
    /^\/administration\/agent\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.settings.agentEdit(ref(m)),
  ],
  [
    /^\/administration\/agent-group(?:\/list)?\/?$/,
    () => APP_PATHS.settings.agentGroups,
  ],
  [
    /^\/administration\/agent-group\/create\/?$/,
    () => APP_PATHS.settings.agentGroupNew,
  ],
  [
    /^\/administration\/agent-group\/archived\/?$/,
    () => APP_PATHS.settings.agentGroupArchived,
  ],
  [
    /^\/administration\/agent-group\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.settings.agentGroupEdit(ref(m)),
  ],
  [/^\/administration\/audit\/?$/, () => APP_PATHS.settings.audit],
  [/^\/administration\/faq\/?$/, () => APP_PATHS.settings.faq],
  [
    /^\/administration\/(?:subscription-meta|subscription-meta-catalog|catalog)\/?$/,
    () => APP_PATHS.settings.company,
  ],
  [
    /^\/administration\/ai-agents(?:\/details)?\/?$/,
    () => APP_PATHS.workflow.aiAgents,
  ],
  [/^\/solution\/company(?:\/list)?\/?$/, () => APP_PATHS.platform.companies],
  [/^\/solution\/company\/create\/?$/, () => APP_PATHS.platform.companyNew],
  [
    /^\/solution\/company\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.platform.company(ref(m)),
  ],
  [
    /^\/solution\/company\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.platform.companyEdit(ref(m)),
  ],
  // Conversations and numbers are managed per organisation now (company detail tabs).
  [
    /^\/solution\/conversations(?:\/list)?\/?$/,
    () => APP_PATHS.platform.companies,
  ],
  [/^\/solution\/manage-phones\/?$/, () => APP_PATHS.platform.companies],
  [/^\/solution\/admin-dashboard\/?$/, () => APP_PATHS.platform.dashboard],
  [/^\/solution\/consommation\/?$/, () => APP_PATHS.platform.usage],
  [
    /^\/solution\/consommation\/export\/?$/,
    () => APP_PATHS.platform.usageExport,
  ],
  [/^\/solution\/recharge\/?$/, () => APP_PATHS.platform.billing],
  [/^\/solution\/recharge\/create\/?$/, () => APP_PATHS.platform.billingNew],
  [/^\/solution\/video-tutorial\/?$/, () => APP_PATHS.platform.tutorials],
  [/^\/solution\/module(?:\/list)?\/?$/, () => APP_PATHS.platform.modules],
  [
    /^\/solution\/module\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.platform.moduleEdit(ref(m)),
  ],
  [
    /^\/solution\/module\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.platform.moduleEdit(ref(m)),
  ],
  [/^\/faq(?:\/list)?\/?$/, () => APP_PATHS.settings.faq],
  [/^\/faq\/import\/?$/, () => APP_PATHS.settings.faqImport],
  [/^\/faq\/create\/?$/, () => APP_PATHS.settings.faqNew],
  [/^\/faq\/view\/([^/]+)\/?$/, (m) => APP_PATHS.settings.faqItem(ref(m))],
  [/^\/faq\/edit\/([^/]+)\/?$/, (m) => APP_PATHS.settings.faqEdit(ref(m))],
  [
    /^\/administration\/faq\/(?:list\/)?\/?$/,
    () => APP_PATHS.settings.faq,
  ],
  [/^\/administration\/faq\/import\/?$/, () => APP_PATHS.settings.faqImport],
  [/^\/administration\/faq\/create\/?$/, () => APP_PATHS.settings.faqNew],
  [
    /^\/administration\/faq\/view\/([^/]+)\/?$/,
    (m) => APP_PATHS.settings.faqItem(ref(m)),
  ],
  [
    /^\/administration\/faq\/edit\/([^/]+)\/?$/,
    (m) => APP_PATHS.settings.faqEdit(ref(m)),
  ],
];

/** First URL segment for old pre-migration routes. */
export const LEGACY_ROUTE_PREFIXES = new Set([
  "analytics-chat",
  "analytics",
  "team-inbox",
  "contact",
  "manage-ticket",
  "order",
  "reservation",
  "product",
  "delivery",
  "voucher",
  "template",
  "automations",
  "broadcast",
  "administration",
  "referential",
  "solution",
  "subscription",
  "faq",
]);

/** First URL segment for new canonical roots that may still use old child segments. */
export const CANONICAL_ROUTE_PREFIXES = new Set([
  "messaging",
  "catalog",
  "workflow",
  "campaigns",
  "settings",
  "platform",
]);

const CANONICAL_SECTION_ROOTS: Record<string, string> = {
  "/messaging": APP_PATHS.messaging.inbox,
  "/catalog": APP_PATHS.catalog.orders,
  "/workflow": APP_PATHS.workflow.templates,
  "/campaigns": APP_PATHS.campaigns.history,
  "/payment": APP_PATHS.payment.links,
  "/settings": APP_PATHS.settings.company,
  "/platform": APP_PATHS.platform.companies,
};

const CANONICAL_REDIRECT_ROOTS = [
  "/messaging/",
  "/catalog/",
  "/workflow/",
  "/campaigns/",
  "/payment/",
  "/settings/",
  "/platform/",
] as const;

const collectAppPaths = (value: unknown): string[] => {
  if (typeof value === "string") {
    return [value.split("?")[0].replace(/\/$/, "") || "/"];
  }
  if (!value || typeof value !== "object") {
    return [];
  }
  return Object.values(value).flatMap(collectAppPaths);
};

const KNOWN_CANONICAL_BASES = collectAppPaths(APP_PATHS)
  .filter((path) => path.startsWith("/"))
  .sort((a, b) => b.length - a.length);

const isKnownCanonicalPath = (path: string): boolean => {
  const normalized = path.replace(/\/$/, "") || "/";
  return KNOWN_CANONICAL_BASES.some(
    (base) => normalized === base || normalized.startsWith(`${base}/`),
  );
};

const isCanonicalRedirectPath = (path: string): boolean =>
  CANONICAL_REDIRECT_ROOTS.some((root) => path.startsWith(root));

/** Maps old view/edit/create/list segments on new canonical base paths. */
const resolveCanonicalOldChildPath = (path: string): string | null => {
  if (!isCanonicalRedirectPath(path)) {
    return null;
  }

  const editMatch = path.match(/^(\/.+)\/edit\/([^/]+)\/?$/);
  if (editMatch) {
    return `${editMatch[1]}/${encodeURIComponent(ref(editMatch, 2))}/edit`;
  }

  const viewMatch = path.match(/^(\/.+)\/view\/([^/]+)\/?$/);
  if (viewMatch) {
    return `${viewMatch[1]}/${encodeURIComponent(ref(viewMatch, 2))}`;
  }

  if (path.endsWith("/create") || path.endsWith("/create/")) {
    return `${path.replace(/\/create\/?$/, "")}/new`;
  }

  if (path.endsWith("/list") || path.endsWith("/list/")) {
    return path.replace(/\/list\/?$/, "") || "/";
  }

  return null;
};

const mergeQuery = (mapped: string, originalQuery: string): string => {
  const [mappedPath, mappedQuery = ""] = mapped.split("?", 2);
  const params = new URLSearchParams(originalQuery);
  new URLSearchParams(mappedQuery).forEach((value, key) => {
    if (!params.has(key)) params.set(key, value);
  });
  const query = params.toString();
  return query ? `${mappedPath}?${query}` : mappedPath;
};

const normalizeLegacyQuery = (path: string, query: string): string => {
  const params = new URLSearchParams(query);
  if (/^\/contact(?:\/list)?\/?$/.test(path)) {
    const search = params.get("search");
    if (search && !params.has("filter")) {
      params.set("filter", JSON.stringify(applyContactQuickSearch({}, search)));
    }
    params.delete("search");
  }
  return params.toString();
};

export function canonicalizeLegacyUrl(url: string): string | null {
  const hashIndex = url.indexOf("#");
  const fragment = hashIndex >= 0 ? url.slice(hashIndex) : "";
  const withoutFragment = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = withoutFragment.indexOf("?");
  const path = (
    queryIndex >= 0 ? withoutFragment.slice(0, queryIndex) : withoutFragment
  ).replace(/\/{2,}/g, "/");
  const query = normalizeLegacyQuery(
    path,
    queryIndex >= 0 ? withoutFragment.slice(queryIndex + 1) : "",
  );

  for (const [pattern, toCanonical] of LEGACY_RULES) {
    const match = path.match(pattern);
    if (match) {
      return `${mergeQuery(toCanonical(match), query)}${fragment}`;
    }
  }

  if (path.startsWith("/referential/delivery-method")) {
    return `${mergeQuery(`${APP_PATHS.catalog.orders}?panel=delivery`, query)}${fragment}`;
  }
  if (path.startsWith("/referential/payment-method")) {
    return `${mergeQuery(`${APP_PATHS.catalog.orders}?panel=payment`, query)}${fragment}`;
  }
  if (path.startsWith("/referential/category-ticket")) {
    return `${mergeQuery(`${APP_PATHS.messaging.tickets}?tab=categories`, query)}${fragment}`;
  }
  if (path.startsWith("/referential/status-ticket")) {
    return `${mergeQuery(`${APP_PATHS.messaging.tickets}?tab=statuses`, query)}${fragment}`;
  }
  if (path.startsWith("/referential/tags-ticket")) {
    return `${mergeQuery(`${APP_PATHS.messaging.tickets}?tab=tags`, query)}${fragment}`;
  }

  const referentialPrefixes: readonly [string, string][] = [
    ["quick-response", APP_PATHS.messaging.quickResponses],
    ["tag", APP_PATHS.messaging.tags],
    ["attribute", APP_PATHS.messaging.attributes],
    ["channel", APP_PATHS.messaging.channels],
    ["variable", APP_PATHS.workflow.templateVariables],
    ["category", APP_PATHS.workflow.templateCategories],
    ["template-folder", APP_PATHS.workflow.templateFolders],
    ["devise", APP_PATHS.settings.currencies],
  ];
  for (const [legacyPrefix, canonicalBase] of referentialPrefixes) {
    const prefix = `/referential/${legacyPrefix}`;
    if (path === prefix || path === `${prefix}/list`) {
      return `${mergeQuery(canonicalBase, query)}${fragment}`;
    }
    const childMatch = path.match(
      new RegExp(`^${prefix}/(?:view/)?([^/]+)/?$`),
    );
    if (childMatch && childMatch[1] !== "create") {
      return `${mergeQuery(`${canonicalBase}/${encodeURIComponent(ref(childMatch))}`, query)}${fragment}`;
    }
    const editMatch = path.match(new RegExp(`^${prefix}/edit/([^/]+)/?$`));
    if (editMatch) {
      return `${mergeQuery(`${canonicalBase}/${encodeURIComponent(ref(editMatch))}/edit`, query)}${fragment}`;
    }
    if (path === `${prefix}/create`) {
      return `${mergeQuery(`${canonicalBase}/new`, query)}${fragment}`;
    }
  }

  const canonicalChild = resolveCanonicalOldChildPath(path);
  if (canonicalChild) {
    return `${mergeQuery(canonicalChild, query)}${fragment}`;
  }

  const sectionRoot = CANONICAL_SECTION_ROOTS[path.replace(/\/$/, "")];
  if (sectionRoot) {
    return `${mergeQuery(sectionRoot, query)}${fragment}`;
  }

  const prefix = path.split("/")[1];
  if (
    prefix &&
    LEGACY_ROUTE_PREFIXES.has(prefix) &&
    !isKnownCanonicalPath(path)
  ) {
    return `${mergeQuery(APP_PATHS.notFound, query)}${fragment}`;
  }

  return null;
}

export function legacyRouteMatcher(
  segments: UrlSegment[],
): UrlMatchResult | null {
  if (segments.length === 0) {
    return null;
  }

  const first = segments[0].path;
  if (LEGACY_ROUTE_PREFIXES.has(first)) {
    return { consumed: segments };
  }

  if (CANONICAL_ROUTE_PREFIXES.has(first)) {
    const path = `/${segments.map((segment) => segment.path).join("/")}`;
    if (canonicalizeLegacyUrl(path)) {
      return { consumed: segments };
    }
  }

  return null;
}

@Injectable({ providedIn: "root" })
export class LegacyRouteRedirectGuard implements CanActivate {
  constructor(private readonly router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): UrlTree {
    const browserFragment =
      typeof window !== "undefined"
        ? window.location.hash.replace(/^#/, "")
        : "";
    const fragment =
      route.fragment ??
      route.root.fragment ??
      this.router.getCurrentNavigation()?.extractedUrl.fragment ??
      (browserFragment || null);
    const legacyUrl =
      fragment && !state.url.includes("#")
        ? `${state.url}#${fragment}`
        : state.url;
    return this.router.parseUrl(
      canonicalizeLegacyUrl(legacyUrl) ?? APP_PATHS.notFound,
    );
  }
}
